import React, { useState, useEffect } from "react";
import { phappsRequest } from "@/services/apiService";
import { API_PATHS } from "@/config/apiConstants";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";

const CreateXeroContactModal = ({ isOpen, onClose }) => {
  const [selectedChemist, setSelectedChemist] = useState(null);
  const [invoicePrefix, setInvoicePrefix] = useState("");
  const [chemists, setChemists] = useState([]);
  const [isFetchingChemists, setIsFetchingChemists] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState(null);

  useEffect(() => {
    if (isOpen) {
      fetchChemists();
    }
  }, [isOpen]);

  const fetchChemists = async () => {
    setIsFetchingChemists(true);
    try {
      const response = await phappsRequest(API_PATHS.CHEMIST_LIST, "GET");
      if (response.success && Array.isArray(response.chemists)) {
        setChemists(response.chemists);
      } else {
        setStatus({ type: "error", message: "Failed to load chemists list" });
      }
    } catch (error) {
      console.error(error);
      setStatus({ type: "error", message: "Error loading chemists" });
    } finally {
      setIsFetchingChemists(false);
    }
  };

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setStatus(null);

    if (!selectedChemist) {
        setStatus({ type: "error", message: "Please select a chemist" });
        setIsLoading(false);
        return;
    }

    try {
      const payload = {
        chemistID: selectedChemist.chemistID,
      };
      if (invoicePrefix) {
        payload.invoicePrefix = invoicePrefix;
      }

      const response = await phappsRequest(
        API_PATHS.CREATE_XERO_CONTACT,
        "POST",
        payload
      );

      if (response.success) {
        setStatus({
          type: "success",
          message: `Xero Contact Created. ID: ${response.xeroContactID}. Prefix Update: ${response.invoicePrefixUpdateStatus}`,
        });
      } else {
        setStatus({ type: "error", message: response.error || "Operation failed" });
      }
    } catch (error) {
      setStatus({ type: "error", message: error.message });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
        <h2 className="text-xl font-bold mb-4">Create Xero Contact</h2>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Chemist</label>
             <Autocomplete
                options={chemists}
                getOptionLabel={(option) => `${option.chemistName} (ID: ${option.chemistID})`}
                value={selectedChemist}
                onChange={(event, newValue) => {
                  setSelectedChemist(newValue);
                }}
                loading={isFetchingChemists}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Search chemist..."
                    variant="outlined"
                    size="small"
                    required={!selectedChemist}
                  />
                )}
                style={{ width: "100%" }}
              />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Invoice Prefix (Optional)
            </label>
            <input
              type="text"
              value={invoicePrefix}
              onChange={(e) => setInvoicePrefix(e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 border p-2"
              placeholder="e.g. PHARM123"
            />
          </div>

          {status && (
            <div
              className={`p-3 rounded-lg ${
                status.type === "success"
                  ? "bg-green-100 text-green-700"
                  : "bg-red-100 text-red-700"
              }`}
            >
              {status.message}
            </div>
          )}

          <div className="flex justify-end gap-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              Close
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {isLoading ? "Creating..." : "Create Contact"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateXeroContactModal;
